import * as React from 'react';

import AdminSisSyncStatusLabel from 'admin/rostering/components/AdminSisSyncStatusLabel';
import Button from 'components/Button';
import CurrentUser from 'auth/CurrentUser';
import TitleHeader from 'components/TitleHeader';
import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import Link from 'components/Link';
import Text from 'components/Text';

import {
  AdminDistrictSisDashboard_organizations_childOrganizations as ChildOrganization,
  AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus as DistrictStatus,
  AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus_schoolMappings as SchoolMapping,
} from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

type Props = {
  orgUuid: string;
  districtStatus: DistrictStatus;
  childOrganizations: ChildOrganization[];
  canOnlySeeReviewScreen: boolean;
  canOnlySeeRosteringStagingArea: boolean;
};

export default class AdminDistrictSisDashboardCleverHeader extends React.Component<Props> {
  numSchoolsToMap = (schoolMappings: SchoolMapping[], childOrgs: ChildOrganization[]) => {
    const numPaidChildOrgs = childOrgs.filter(org => org.paidAt).length;
    const numMappedChildOrgs = schoolMappings.filter(mapping => mapping.remindSchoolUuid).length;
    return numPaidChildOrgs - numMappedChildOrgs;
  };

  renderMatchingBanner = () => {
    const { orgUuid } = this.props;

    const button = (
      <Button
        eventElementName="AdminDistrictSisDashboardCleverHeader-matchYourSchools"
        href={`/sync_setup/matching?district_uuid=${orgUuid}`}>
        Match schools
      </Button>
    );
    return (
      <AdminNoticeBanner glyph="success-checkmark-stroke" color="brand" actionElement={button}>
        Finish setup by mapping your schools from Clever correctly.
      </AdminNoticeBanner>
    );
  };

  renderAwaitingReviewBanner = () => {
    return (
      <AdminNoticeBanner glyph="clock" color="sunrise" actionElement={null}>
        Our implementation team is reviewing your data. You'll receive an email with next steps in
        two business days.
      </AdminNoticeBanner>
    );
  };

  renderStagingEnvironmentBanner = () => {
    return (
      <AdminNoticeBanner glyph="clock" color="koala" actionElement={null}>
        Review each school's sync to fix errors. When you're ready to push data live, contact our
        implementation team.
      </AdminNoticeBanner>
    );
  };

  renderSubtitle = (showSetupBanner: boolean, isNotActive: boolean) => {
    const { orgUuid } = this.props;

    if (showSetupBanner) {
      return (
        <Text>
          Complete the <Link href={`/sync_setup?district_uuid=${orgUuid}`}>Clever setup </Link> to
          send your school data to Remind.
        </Text>
      );
    }
    if (!isNotActive) {
      return <Text>Click on a school to see details.</Text>;
    }
  };

  renderBanner = (noSchoolsMapped: boolean, showMatchingBanner: boolean) => {
    const { canOnlySeeReviewScreen, canOnlySeeRosteringStagingArea } = this.props;

    if (noSchoolsMapped) {
      return null;
    }

    if (showMatchingBanner) {
      return this.renderMatchingBanner();
    }

    if (canOnlySeeReviewScreen) {
      return this.renderAwaitingReviewBanner();
    }

    if (canOnlySeeRosteringStagingArea) {
      return this.renderStagingEnvironmentBanner();
    }

    return null;
  };

  renderTitle = (isNotActive: boolean) => {
    if (isNotActive) {
      return <AdminSisSyncStatusLabel status="pending" />;
    }
    return <AdminSisSyncStatusLabel status="active" />;
  };

  render() {
    const {
      childOrganizations,
      orgUuid,
      districtStatus,
      canOnlySeeReviewScreen,
      canOnlySeeRosteringStagingArea,
    } = this.props;

    const { schoolMappings } = districtStatus;

    const noSchoolsMapped = schoolMappings.length === 0;
    const someSchoolsNotMapped = this.numSchoolsToMap(schoolMappings, childOrganizations) > 0;

    const showMatchingBanner = !noSchoolsMapped && someSchoolsNotMapped;
    const isNotActive = noSchoolsMapped || canOnlySeeReviewScreen || canOnlySeeRosteringStagingArea;

    return (
      <TitleHeader
        title={this.renderTitle(isNotActive)}
        subtext={this.renderSubtitle(noSchoolsMapped, isNotActive)}
        actionPlacement="right"
        className="AdminDistrictSisDashboardCleverHeader">
        {this.renderBanner(noSchoolsMapped, showMatchingBanner)}
      </TitleHeader>
    );
  }
}
